import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartState } from "./ChartState";
import { formatAxis, rollingAvg } from "./utils";
import { useTimeseries } from "@shared/hooks/useTimeseries";

const formatKd = (value: number | string) =>
  Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });

export function KdOverTime({ from, useDaily, refreshKey }: { from: number | undefined; useDaily: boolean; refreshKey: number }) {
  const { data, error } = useTimeseries({ metric: "kd", bucket: useDaily ? "day" : "match", from, refreshKey });

  const points = useMemo(() => {
    if (!data || data.length === 0) return [];
    const values = data.map((point) => point.v);
    const windowSize = useDaily ? 7 : 20;
    return data.map((point, index) => ({
      t: point.t,
      kd: point.v,
      kdAvg: rollingAvg(values, index, windowSize),
    }));
  }, [data, useDaily]);

  if (data === null) return <ChartState>Loading chart...</ChartState>;
  if (data.length === 0) return <ChartState>{error ? `Could not load chart: ${error}` : "No tracked match data yet."}</ChartState>;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={points}>
        <CartesianGrid stroke="#30363d" strokeDasharray="3 3" />
        <XAxis dataKey="t" stroke="#8b949e" tick={{ fontSize: 11 }} tickFormatter={(v) => formatAxis(v, useDaily)} minTickGap={40} />
        <YAxis stroke="#8b949e" tick={{ fontSize: 11 }} tickFormatter={formatKd} width={40} />
        <Tooltip contentStyle={{ background: "#161b22", border: "1px solid #30363d" }} labelFormatter={(v) => formatAxis(v as number | string, useDaily)} formatter={formatKd} />
        <Line type="monotone" dataKey="kd" stroke="#f97316" strokeOpacity={0.4} dot={false} name="K/D" />
        <Line type="monotone" dataKey="kdAvg" stroke="#f97316" strokeWidth={2} dot={false} name={useDaily ? "7d avg" : "20-match avg"} />
      </LineChart>
    </ResponsiveContainer>
  );
}
